import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as Sentry from "@sentry/react";
import React, { useState } from "react";

// eslint-disable-next-line @typescript-eslint/no-var-requires
const logo: string = require("../assets/logo_wilya_dark_plain.svg").default;

interface IErrorBoundaryProps {
    children?: React.ReactNode;
}

interface IErrorBoundaryState {
    hasError: boolean;
    eventId?: string;
}

class ErrorBoundary extends React.Component<IErrorBoundaryProps, IErrorBoundaryState> {
    constructor(props: any) {
        super(props);
        this.state = { hasError: false, eventId: undefined };
    }

    static getDerivedStateFromError(error: any) {
        return { hasError: true };
    }

    componentDidCatch(error: any, errorInfo: any) {
        localStorage.clear();
        const eventId = Sentry.captureException(error, { extra: errorInfo });
        this.setState({ eventId });
    }

    render() {
        if (this.state.hasError) {
            return <SomethingWentWrong eventId={this.state.eventId} />;
        }

        return this.props.children;
    }
}

interface ISomethingWentWrongProps {
    eventId?: string;
}

const SomethingWentWrong: React.FC<ISomethingWentWrongProps> = ({ eventId }) => {
    const [copied, setCopied] = useState(false);

    const supportEmail = "help@wilya.com";
    const subject = encodeURIComponent(`Crash Report - Error ID: [${eventId || "N/A"}]`);
    const body = encodeURIComponent(
        `Please describe what you were doing when the crash happened:\n\n\n\n---\nError ID: ${
            eventId || "N/A"
        }`
    );

    const mailtoLink = `mailto:${supportEmail}?subject=${subject}&body=${body}`;

    const handleCopy = () => {
        if (eventId) {
            navigator.clipboard.writeText(eventId);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
            alignItems="center"
            pt={16}
            px={3}
            height="100vh"
            width="100%"
            bgcolor="#f5f5f5"
            textAlign="left"
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Box>
                    <img src={logo} alt="Logo" height={75} />
                    <Box px={1.75} mt={4}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "flex-start",
                                alignItems: "center",
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontSize: "15px",
                                    fontWeight: 500,
                                    color: "rgba(0,0,0,0.87)",
                                }}
                            >
                                Error Encountered.
                            </Typography>
                            <Typography
                                sx={{
                                    fontFamily: "Roboto",
                                    fontSize: "15px",
                                    fontWeight: 400,
                                    color: "rgba(0,0,0,0.57)",
                                    marginLeft: "4px",
                                }}
                            >
                                {"An unexpected issue has occurred."}
                            </Typography>
                        </Box>
                        <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontSize: "15px",
                                fontWeight: 400,
                                color: "rgba(0,0,0,0.87)",
                                marginTop: "32px",
                            }}
                        >
                            {"We have encountered an issue that prevents the system from operating correctly."}
                        </Typography>
                        <Typography
                            sx={{
                                fontFamily: "Roboto",
                                fontSize: "15px",
                                fontWeight: 400,
                                color: "rgba(0,0,0,0.57)",
                                mb: 3,
                            }}
                        >
                            {"Please try again later, or contact support if the issue persists."}
                        </Typography>

                        <Button
                            variant="contained"
                            color="primary"
                            href={mailtoLink}
                            sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                mb: 4,
                            }}
                        >
                            Report this issue
                        </Button>

                        {eventId && (
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 1,
                                    mt: 2,
                                    p: 2,
                                    bgcolor: "rgba(0, 0, 0, 0.04)",
                                    borderRadius: 1,
                                    maxWidth: "fit-content",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "rgba(0, 0, 0, 0.54)",
                                        fontWeight: 500,
                                    }}
                                >
                                    Error ID for reference:
                                </Typography>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: "monospace",
                                            fontSize: "14px",
                                            color: "rgba(0, 0, 0, 0.87)",
                                        }}
                                    >
                                        {eventId}
                                    </Typography>
                                    <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                                        <Button
                                            onClick={handleCopy}
                                            size="small"
                                            sx={{ minWidth: "auto", p: 0.5 }}
                                        >
                                            <ContentCopyIcon sx={{ fontSize: 18 }} />
                                        </Button>
                                    </Tooltip>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default ErrorBoundary;